import { SelectBrandItem } from "../../services/dashboard.service";
import { PerformanceComparisonMetric } from "../my-brand-screen/types";
import { SuggestedQuestion } from "../copilot/types";

export type AlertOption = {
  name: string;
  description?: string;
  subDescription?: string;
  formatMetric?: PerformanceComparisonMetric;
};

export enum AlertMetric {
  IMPRESSIONS = 'impressions',
  SPEND = 'spend'
}

export enum AlertFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly'
}

export enum AlertType {
  SPIKE = 'spike',
  DROP = 'drop'
}

export enum PosthogEventNames {
  ALERT_ACTIVATED = 'alert_activated',
  ALERT_DEACTIVATED = 'alert_deactivated',
}

export enum PosthogEventProperties {
  ALERT_TYPE = 'alert_type',
}

export enum PosthogEventPropertyValues {
  ACTIVITY_SPIKE = 'activity-spike',
  ACTIVITY_DROP = 'activity-drop',
}

export const AlertStepsMap: Record<number, AlertOption> = {
  1: {
    name: 'Frequency',
    description: 'How often would you like to receive these alerts?'
  },
  2: {
    name: 'Brand Selection',
    description: 'Select your brand and competitors to monitor'
  },
  3: {
    name: 'Metrics',
    description: 'Choose which metrics to track'
  },
  4: {
    name: 'Review',
    description: 'Confirm your settings before activating',
    subDescription: 'You can modify these settings at any time'
  }
};

export const AlertTypeMap: Record<AlertType, AlertOption> = {
  [AlertType.SPIKE]: {
    name: 'Activity Spike',
    description: 'Get notified when ad activity increases significantly'
  },
  [AlertType.DROP]: {
    name: 'Activity Drop',
    description: 'Get notified when ad activity decreases significantly'
  }
};

export const AlertFrequencyMap: Record<AlertFrequency, AlertOption> = {
  [AlertFrequency.DAILY]: {
    name: 'Daily Digest',
    description: 'Summary of all alerts once per day'
  },
  [AlertFrequency.WEEKLY]: {
    name: 'Weekly Digest',
    description: 'Comprehensive weekly summary'
  }
};

export const AlertMetricMap: Record<AlertMetric, AlertOption> = {
  [AlertMetric.IMPRESSIONS]: {
    name: 'Impressions',
    formatMetric: PerformanceComparisonMetric.IMPRESSIONS
  },
  [AlertMetric.SPEND]: {
    name: 'Spend',
    formatMetric: PerformanceComparisonMetric.SPEND
  }
};

export type AlertSubscriptionPayload = {
  subscriptionId: string | undefined; // optional for create operation
  accountId: string;
  alertType: AlertType;
  metric: AlertMetric;
  brands: string[];
  frequency: AlertFrequency;
  enabled: boolean;
}

export type AlertSubscriptionResponse = {
  subscriptionId: string;
  alertType: AlertType;
  metric: AlertMetric;
  brands: string[];
  frequency: AlertFrequency;
  enabled: boolean;
}

export type AlertSubscriptionsListResponse = {
  alerts: AlertSubscriptionResponse[];
}

export type GetAlertSubscriptionResponse = {
  alertEventId: string;
  alertType: AlertType;
  brand: string;
  brandKey: string;
  logoPath: string | null;
  metric: AlertMetric;
  dateBefore: string;
  dateAfter: string;
  valueBefore: number;
  valueAfter: number;
  percentChange: number;
  trendSeries: {
    date: string;
    value: number;
  }[];
}

export type EmptyAlertSubscriptionResponse = {
  subscriptionId: string | undefined;
  alertType: AlertType;
  metric: AlertMetric | undefined;
  brands: string[];
  frequency: AlertFrequency | undefined;
  enabled: boolean;
}

export type AlertSubscriptionList = {
  alerts: (EmptyAlertSubscriptionResponse|AlertSubscriptionResponse)[];
}

export const BRAND_QUESTION_TOKEN = '$brand';
export const POLARIS_ALERTS_SUGGESTED_QUESTIONS_AMOUNT: number = 3;
export const POLARIS_ALERTS_SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
  'What may have caused this change?',
  'What was the main driver of this change?',
  `Has ${BRAND_QUESTION_TOKEN} changed the targeting strategy?`,
  `Which creatives were running by ${BRAND_QUESTION_TOKEN} during this period?`,
  'Which dimension changed the most: publisher, format, device, or geography?',
  'How does this change compare to the typical week-over-week variance for this brand?'
];

export interface BrandRowData extends SelectBrandItem {
  isSelected: boolean;
}

export interface BrandSelectionRowData {
  myBrand: BrandRowData;
  competitors: BrandRowData[];
}


